import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import styled, { useTheme } from 'styled-components';

import { Button, Icon, Modal, ModalSize, Tooltip } from '@column/column-ui-kit';

import { CodeInput, Loading } from '~/components';
import { Box, Grid, Inner } from '~/styles';

/**
 * TEMPORARY preview route (`/setup-mfa-preview`) for layout-checking the 2FA setup modal.
 * The real `SetupDeviceMFA` fetches its otpauth URI from the backend, so this is a static
 * replica of its code-input step (styled components copied from `SetupDeviceMFA.tsx`) with
 * a mock QR. Entering 6 digits simulates a successful verification so the success-confirmation
 * animation can be previewed. Remove this file and its route in `public/Router.tsx`.
 */

const MOCK_OTPAUTH_URI =
  'otpauth://totp/Column:trosage@column.com?secret=JBSWY3DPEHPK3PXP&issuer=Column&algorithm=SHA1&digits=6&period=30';

// --- styled components copied verbatim from SetupDeviceMFA.tsx (non-fullWidth path) ---

const CodeInputDescription = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  text-wrap: pretty;
`;

const StyledTooltip = styled(Tooltip)<{ $fullWidth?: boolean }>`
  display: inline;
  width: ${({ $fullWidth }) => ($fullWidth ? 'auto' : '100%')};
`;

const VerificationOverview = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  font-size: 14px;
  justify-content: center;
  padding: 0;
  width: 100%;

  p {
    max-width: 440px;
    margin: 0;
    text-align: center;
    color: ${({ theme }) => theme.secondary.background};
  }
`;

const MfaWrapper = styled.div<{ $fullWidth?: boolean }>`
  display: grid;
  grid-template-columns: auto;
  max-width: none;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: min-content minmax(0, 1fr);
  }
`;

const MfaQRCode = styled.div<{ $fullWidth?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 32px 24px 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 24px;
    border-right: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
    box-sizing: border-box;
  }
`;

const MfaInput = styled.div<{ $fullWidth?: boolean }>`
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: flex-start;
    text-align: left;
  }
`;

const MfaSetupCode = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: start;
    flex-direction: column;
  }

  p {
    margin: 0 0 2px;
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.secondary.blendToBackground(600)};
    text-align: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      text-align: left;
    }
  }
`;

const StyledBox = styled(Box)<{ $fullWidth?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;

export const SetupMfaPreview: React.FC = () => {
  const theme = useTheme();
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [succeeded, setSucceeded] = useState<boolean>(false);

  // Simulate a successful verification once six digits are entered so the confirmation
  // animation can be previewed; reset back to the setup view when the animation finishes.
  const handleChange = (value: string) => {
    setVerificationCode(value);
    if (value.length === 6) {
      setSucceeded(true);
    }
  };

  // Mirrors the real modal: hold the inline success check for a beat, then reset back
  // to the setup view so the animation can be re-tested.
  useEffect(() => {
    if (!succeeded) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setSucceeded(false);
      setVerificationCode('');
    }, 1800);
    return () => clearTimeout(timer);
  }, [succeeded]);

  return (
    <Modal onClose={() => undefined} isOpen size={ModalSize.Medium}>
      <Modal.IconCircle type="primary">
        <Icon.Key />
      </Modal.IconCircle>

      <Modal.Content>
        <Modal.Headline fullWidth align="center">
          Setup Authenticator App
        </Modal.Headline>

        <Inner p={0} pb={24}>
          <Loading isLoading={false}>
            <Grid vertical>
              <VerificationOverview>
                <p>
                  Scan the QR code below with your preferred authenticator app. We recommend 1Password, Authy, or Google
                  Authenticator.
                </p>
              </VerificationOverview>
              <StyledBox variant="secondary">
                <MfaWrapper>
                  <MfaQRCode>
                    <QRCode fgColor={theme.foreground} bgColor="transparent" size={150} value={MOCK_OTPAUTH_URI} />
                  </MfaQRCode>
                  <MfaInput>
                    <CodeInputDescription>Enter the code generated by your app</CodeInputDescription>
                    <CodeInput
                      fullWidth
                      hideCaret
                      isComplete={succeeded}
                      isDisabled={succeeded}
                      placeholder="######"
                      showPlaceholderOnFocus
                      type="number"
                      value={verificationCode}
                      onChange={handleChange}
                    />

                    <MfaSetupCode>
                      <p>If your authenticator doesn't recognize the QR code, copy this setup code</p>
                      <StyledTooltip content={'Copied!'} placement={'bottom'} zIndex={100} isOpen={false}>
                        <Button variant="secondary" size="tiny" onClick={() => undefined}>
                          Copy setup code
                        </Button>
                      </StyledTooltip>
                    </MfaSetupCode>
                  </MfaInput>
                </MfaWrapper>
              </StyledBox>
            </Grid>
          </Loading>
        </Inner>
      </Modal.Content>
    </Modal>
  );
};
